import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  List,
  Button,
  IconButton,
  Typography,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
} from "@mui/material";
import { useClub } from "../../../contexts/ClubContext";
import { useSchemes } from "../../../contexts/SchemesContext";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { PersonnelGroup } from "../../../types/DepthChartTypes";
import { Front, Stunt, Coverage } from "../../../types/SchemesTypes";

// TabPanel component for tab content
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scheme-tabpanel-${index}`}
      aria-labelledby={`scheme-tab-${index}`}
      {...other}
      style={{ padding: "16px 0" }}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const DefenseSchemes = () => {
  const [tabValue, setTabValue] = useState(0);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const { club } = useClub();

  const [currentPersonnelGroup, setCurrentPersonnelGroup] = useState<
    Partial<PersonnelGroup>
  >({ name: "", side: "DEFENSE" });
  const [currentFront, setCurrentFront] = useState<Partial<Front>>({
    name: "",
    description: "",
    club: club.id,
  });
  const [currentStunt, setCurrentStunt] = useState<Partial<Stunt>>({
    name: "",
    description: "",
    club: club.id,
  });
  const [currentCoverage, setCurrentCoverage] = useState<Partial<Coverage>>({
    name: "",
    description: "",
    club: club.id,
  });

  // Club context

  // Schemes context
  const {
    defensivePersonnel,
    fronts,
    stunts,
    coverages,
    savePersonnelGroup,
    deletePersonnelGroup,
    saveFront,
    saveStunt,
    saveCoverage,
    deleteFront,
    deleteStunt,
    deleteCoverage,
  } = useSchemes();

  // Dialog states
  const [personnelDialogOpen, setPersonnelDialogOpen] = useState(false);
  const [frontsDialogOpen, setFrontsDialogOpen] = useState(false);
  const [stuntsDialogOpen, setStuntsDialogOpen] = useState(false);
  const [coveragesDialogOpen, setCoveragesDialogOpen] = useState(false);

  const handleSavePersonnelGroup = async () => {
    try {
      if (!currentPersonnelGroup.name) {
        setAlert({
          open: true,
          message: "Name is required",
          severity: "error",
        });
        return;
      }

      await savePersonnelGroup({
        id: currentPersonnelGroup.id,
        name: currentPersonnelGroup.name,
        side: "DEFENSE",
      });

      setAlert({
        open: true,
        message: "Personnel group saved successfully",
        severity: "success",
      });

      setPersonnelDialogOpen(false);
    } catch (error) {
      console.error("Error saving personnel group:", error);
      setAlert({
        open: true,
        message: "Failed to save personnel group",
        severity: "error",
      });
    }
  };

  const handleSaveFront = async () => {
    try {
      if (!currentFront.name) {
        setAlert({
          open: true,
          message: "Name is required",
          severity: "error",
        });
        return;
      }

      await saveFront({
        id: currentFront.id,
        name: currentFront.name,
        description: currentFront.description || "",
        club: club.id,
      });

      setAlert({
        open: true,
        message: "Front saved successfully",
        severity: "success",
      });

      setFrontsDialogOpen(false);
    } catch (error) {
      console.error("Error saving front:", error);
      setAlert({
        open: true,
        message: "Failed to save front",
        severity: "error",
      });
    }
  };

  const handleSaveStunt = async () => {
    try {
      if (!currentStunt.name) {
        setAlert({
          open: true,
          message: "Name is required",
          severity: "error",
        });
        return;
      }

      await saveStunt({
        id: currentStunt.id,
        name: currentStunt.name,
        description: currentStunt.description || "",
        club: club.id,
      });

      setAlert({
        open: true,
        message: "Stunt saved successfully",
        severity: "success",
      });

      setStuntsDialogOpen(false);
    } catch (error) {
      console.error("Error saving stunt:", error);
      setAlert({
        open: true,
        message: "Failed to save stunt",
        severity: "error",
      });
    }
  };

  const handleSaveCoverage = async () => {
    try {
      if (!currentCoverage.name) {
        setAlert({
          open: true,
          message: "Name is required",
          severity: "error",
        });
        return;
      }

      await saveCoverage({
        id: currentCoverage.id,
        name: currentCoverage.name,
        description: currentCoverage.description || "",
        club: club.id,
      });

      setAlert({
        open: true,
        message: "Coverage saved successfully",
        severity: "success",
      });

      setCoveragesDialogOpen(false);
    } catch (error) {
      console.error("Error saving coverage:", error);
      setAlert({
        open: true,
        message: "Failed to save coverage",
        severity: "error",
      });
    }
  };

  const handleOpenPersonnelDialog = (group?: PersonnelGroup) => {
    if (group) {
      setCurrentPersonnelGroup(group);
    } else {
      setCurrentPersonnelGroup({ name: "", side: "DEFENSE" });
    }
    setPersonnelDialogOpen(true);
  };

  const handleOpenFrontsDialog = (front?: Front) => {
    if (front) {
      setCurrentFront(front);
    } else {
      setCurrentFront({ name: "", description: "", club: club.id });
    }
    setFrontsDialogOpen(true);
  };

  const handleOpenCoveragesDialog = (coverage?: Coverage) => {
    if (coverage) {
      setCurrentCoverage(coverage);
    } else {
      setCurrentCoverage({ name: "", description: "", club: club.id });
    }
    setCoveragesDialogOpen(true);
  };

  const handleOpenStuntsDialog = (stunt?: Stunt) => {
    if (stunt) {
      setCurrentStunt(stunt);
    } else {
      setCurrentStunt({ name: "", description: "", club: club.id });
    }
    setStuntsDialogOpen(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  return (
    <Box
      sx={{
        p: 1,
        maxWidth: 1600,
        width: "100%",
        bgcolor: "#121212",
        color: "white",
        height: "100vh",
        boxSizing: "border-box",
      }}
    >
      {/* Tabs for configuration types */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "rgba(123, 45, 58, 0.9)",
            },
            "& .MuiTab-root": {
              color: "rgba(255, 255, 255, 0.7)",
              "&.Mui-selected": {
                color: "white",
              },
            },
          }}
        >
          <Tab label="Personnel Groups" id="scheme-tab-0" />
          <Tab label="Fronts" id="scheme-tab-1" />
          <Tab label="Stunts" id="scheme-tab-2" />
          <Tab label="Coverages" id="scheme-tab-3" />
        </Tabs>
      </Box>

      {/* Personnel Groups Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenPersonnelDialog()}
            sx={{
              color: "white",
              bgcolor: "rgba(123, 45, 58, 0.9)",
              "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
              fontWeight: 600,
            }}
          >
            Add Group
          </Button>
        </Box>

        <List
          sx={{
            flex: 1,
            bgcolor: "#232323",
            borderRadius: 1,
            p: 2,
            minWidth: 0,
          }}
        >
          {defensivePersonnel.map((group) => (
            <ListItem
              key={group.id}
              secondaryAction={
                <Box>
                  <IconButton
                    edge="end"
                    onClick={() => handleOpenPersonnelDialog(group)}
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => group.id && deletePersonnelGroup(group.id)}
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
              sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
            >
              <ListItemText
                primary={<Typography color="white">{group.name}</Typography>}
              />
            </ListItem>
          ))}
          {defensivePersonnel.length === 0 && (
            <ListItem
              sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
            >
              <ListItemText
                primary={
                  <Typography color="rgba(255, 255, 255, 0.7)">
                    No personnel groups found
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </TabPanel>

      {/* Fronts Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenFrontsDialog()}
            sx={{
              color: "white",
              bgcolor: "rgba(123, 45, 58, 0.9)",
              "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
              fontWeight: 600,
            }}
          >
            Add Front
          </Button>
        </Box>

        <List
          sx={{
            flex: 1,
            bgcolor: "#232323",
            borderRadius: 1,
            p: 2,
            minWidth: 0,
          }}
        >
          {fronts.map((front) => (
            <ListItem
              key={front.id}
              secondaryAction={
                <Box>
                  <IconButton
                    edge="end"
                    onClick={() => handleOpenFrontsDialog(front)}
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => front.id && deleteFront(front.id)}
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
              sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
            >
              <ListItemText
                primary={<Typography color="white">{front.name}</Typography>}
              />
            </ListItem>
          ))}
          {fronts.length === 0 && (
            <ListItem
              sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
            >
              <ListItemText
                primary={
                  <Typography color="rgba(255, 255, 255, 0.7)">
                    No fronts found
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </TabPanel>

      {/* Stunts Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenStuntsDialog()}
            sx={{
              color: "white",
              bgcolor: "rgba(123, 45, 58, 0.9)",
              "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
              fontWeight: 600,
            }}
          >
            Add Stunt
          </Button>
        </Box>

        <List
          sx={{
            flex: 1,
            bgcolor: "#232323",
            borderRadius: 1,
            p: 2,
            minWidth: 0,
          }}
        >
          {stunts.map((stunt) => (
            <ListItem
              key={stunt.id}
              secondaryAction={
                <Box>
                  <IconButton
                    edge="end"
                    onClick={() => handleOpenStuntsDialog(stunt)}
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => stunt.id && deleteStunt(stunt.id)}
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
              sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
            >
              <ListItemText
                primary={<Typography color="white">{stunt.name}</Typography>}
              />
            </ListItem>
          ))}
          {stunts.length === 0 && (
            <ListItem
              sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
            >
              <ListItemText
                primary={
                  <Typography color="rgba(255, 255, 255, 0.7)">
                    No stunts found
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </TabPanel>

      {/* Coverages Tab */}
      <TabPanel value={tabValue} index={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenCoveragesDialog()}
            sx={{
              color: "white",
              bgcolor: "rgba(123, 45, 58, 0.9)",
              "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
              fontWeight: 600,
            }}
          >
            Add Coverage
          </Button>
        </Box>

        <List
          sx={{
            flex: 1,
            bgcolor: "#232323",
            borderRadius: 1,
            p: 2,
            minWidth: 0,
          }}
        >
          {coverages.map((coverage) => (
            <ListItem
              key={coverage.id}
              secondaryAction={
                <Box>
                  <IconButton
                    edge="end"
                    onClick={() => handleOpenCoveragesDialog(coverage)}
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => coverage.id && deleteCoverage(coverage.id)}
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
              sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
            >
              <ListItemText
                primary={<Typography color="white">{coverage.name}</Typography>}
              />
            </ListItem>
          ))}
          {coverages.length === 0 && (
            <ListItem
              sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
            >
              <ListItemText
                primary={
                  <Typography color="rgba(255, 255, 255, 0.7)">
                    No coverages found
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </TabPanel>

      {/* Personnel Group Dialog */}
      <Dialog
        open={personnelDialogOpen}
        onClose={() => setPersonnelDialogOpen(false)}
      >
        <DialogTitle>
          {currentPersonnelGroup.id
            ? "Edit Personnel Group"
            : "Add Personnel Group"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentPersonnelGroup.name}
              onChange={(e) =>
                setCurrentPersonnelGroup({
                  ...currentPersonnelGroup,
                  name: e.target.value,
                })
              }
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPersonnelDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSavePersonnelGroup}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fronts Dialog */}
      <Dialog
        open={frontsDialogOpen}
        onClose={() => setFrontsDialogOpen(false)}
      >
        <DialogTitle>
          {currentFront.id ? "Edit Front" : "Add Front"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentFront.name}
              onChange={(e) =>
                setCurrentFront({
                  ...currentFront,
                  name: e.target.value,
                })
              }
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFrontsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveFront} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stunts Dialog */}
      <Dialog
        open={stuntsDialogOpen}
        onClose={() => setStuntsDialogOpen(false)}
      >
        <DialogTitle>
          {currentStunt.id ? "Edit Stunt" : "Add Stunt"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentStunt.name}
              onChange={(e) =>
                setCurrentStunt({
                  ...currentStunt,
                  name: e.target.value,
                })
              }
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStuntsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveStunt} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Coverages Dialog */}
      <Dialog
        open={coveragesDialogOpen}
        onClose={() => setCoveragesDialogOpen(false)}
      >
        <DialogTitle>
          {currentCoverage.id ? "Edit Coverage" : "Add Coverage"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentCoverage.name}
              onChange={(e) =>
                setCurrentCoverage({
                  ...currentCoverage,
                  name: e.target.value,
                })
              }
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoveragesDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveCoverage}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
